import { useState } from 'react'

import { Avatar, Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useCardsDataTable,
} from 'generated/resources/datatable.graphql'
import { BankingCardFilter, CardholderForCardsDataTableFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { CardSidepanel } from 'modules/banking/components/CardSidepanel'

import { Actions } from './_Actions'
import { Alias } from './_Alias'
import { CardType } from './_CardType'
import { Cardholder } from './_Cardholder'
import { CreationSource } from './_CreationSource'
import { Last4 } from './_Last4'
import { Status } from './_Status'

interface Props {
  cardholders: CardholderForCardsDataTableFragment[]
  companyId: string
  bankAccountId?: string
  tableIdentifier: string
  canAddCard?: boolean
}

export const CardsDataTable = ({
  cardholders,
  companyId,
  bankAccountId,
  tableIdentifier,
  canAddCard,
}: Props) => {
  const { status, showCard: showCardValue } = useSearchParams<{
    status?: string
    showCard?: string
  }>()

  const uniqueCardholders = (() => {
    const seenEmployeeIds = new Set<string>()
    return cardholders.filter((cardholder) => {
      const employeeId = cardholder.employee?.id
      if (!employeeId || seenEmployeeIds.has(employeeId)) {
        return false
      }
      seenEmployeeIds.add(employeeId)
      return true
    })
  })()

  const cardholderOptions = uniqueCardholders.map((cardholder) => {
    const employee = cardholder.employee
    const name = employee ? employee.fullName : ''
    const avatar = employee ? employee.avatar : undefined
    const avatarUrl = avatar ? buildUrl(avatar.url) : undefined
    const component = (
      <Box gap="s8" flexDirection="row" alignItems="center">
        <Avatar label={name} url={avatarUrl} size="s32" />
        <Text.Normal size="300">{name}</Text.Normal>
      </Box>
    )

    return {
      label: name,
      value: cardholder.id,
      component: component,
    }
  })

  const { navigation } = useNavigation()
  const [sidepanelCardId, setSidepanelCardId] = useState<string | null>(
    showCardValue ? String(showCardValue) : null
  )

  const bankAccountIds = bankAccountId ? [String(bankAccountId)] : []

  const source = useCardsDataTable(
    [
      {
        identifier: 'cardholder',
        title: i18n.t('expenses_v2.cards.table.cardholder'),
        kind: ProjectionKind.Custom,
        template: '{{ cardholder.employee.access.fullName }}',
        width: 250,
        render: (card) => {
          return <Cardholder card={card} />
        },
      },
      {
        identifier: 'last4',
        title: i18n.t('expenses_v2.cards.table.last4'),
        kind: ProjectionKind.Custom,
        template: '{{ last4 }}',
        render: (card) => {
          return <Last4 card={card} />
        },
      },
      {
        identifier: 'alias',
        title: i18n.t('expenses_v2.cards.table.alias'),
        kind: ProjectionKind.Custom,
        template: '{{ alias }}',
        render: (card) => {
          return <Alias card={card} />
        },
      },
      {
        identifier: 'type',
        title: i18n.t('expenses_v2.cards.table.card_type'),
        kind: ProjectionKind.Custom,
        template: '{{ type }}',
        render: (card) => {
          return <CardType card={card} />
        },
      },
      {
        identifier: 'creation_source',
        title: i18n.t('expenses_v2.cards.table.creation_source'),
        kind: ProjectionKind.Custom,
        template: '{{ creationSource }}',
        render: (card) => {
          return <CreationSource card={card} />
        },
      },
      {
        identifier: 'status',
        title: i18n.t('expenses_v2.cards.table.status'),
        kind: ProjectionKind.Custom,
        template: '{{ status }}',
        render: (card) => {
          return <Status card={card} />
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: (card) => {
          return <Actions bankAccountId={bankAccountId} card={card} />
        },
      },
    ],
    {
      variables: {
        bankAccountIds,
        companyId,
      },
    }
  )

  source.identifier = tableIdentifier

  const EmptyState = () => (
    <>
      <Icon.Large icon="document-folder-drawer-2" color="grey600" />
      <Box flexDirection="column" gap="s4" alignItems="center">
        <Text.Header2>{i18n.t('expenses_v2.cards.table.empty_table.title')}</Text.Header2>
        <Text.Normal color="grey800">
          {i18n.t('expenses_v2.cards.table.empty_table.subtitle')}
        </Text.Normal>
        <Text.Normal color="grey800">
          {i18n.t('expenses_v2.cards.table.empty_table.description')}
        </Text.Normal>
      </Box>
    </>
  )

  const filters: DataTableFilter<BankingCardFilter>[] = [
    {
      identifier: 'cardTypeFilter',
      title: i18n.t('banking.attributes.type'),
      path: 'type',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('expenses_v2.cards.type.short_physical'),
              value: 'physical',
            },
            {
              label: i18n.t('expenses_v2.cards.type.short_virtual'),
              value: 'virtual',
            },
            {
              label: i18n.t('expenses_v2.cards.type.short_single_use'),
              value: 'single_use',
            },
          ],
        },
      ],
    },
    {
      identifier: 'statusFilter',
      title: i18n.t('expenses_v2.cards.table.status'),
      path: 'status',
      default: !!status,
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('expenses_v2.cards.status.pending'),
              value: 'pending',
            },
            {
              label: i18n.t('expenses_v2.cards.status.active'),
              value: 'active',
            },
            {
              label: i18n.t('expenses_v2.cards.status.inactive'),
              value: 'inactive',
            },
            {
              label: i18n.t('expenses_v2.cards.status.requested'),
              value: 'requested',
            },
            {
              label: i18n.t('expenses_v2.cards.status.rejected'),
              value: 'rejected',
            },
            {
              label: i18n.t('expenses_v2.cards.status.canceled'),
              value: 'canceled',
            },
          ],
          value: status ? [status] : undefined,
        },
      ],
    },
    {
      identifier: 'creationSourceFilter',
      title: i18n.t('expenses_v2.cards.table.creation_source'),
      path: 'creationSource',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('banking.cards.creation_source.expenses'),
              value: 'expenses',
            },
            {
              label: i18n.t('banking.cards.creation_source.software'),
              value: 'software',
            },
            {
              label: i18n.t('banking.accounts.card_creation_source.procurement'),
              value: 'procurement',
            },
            {
              label: i18n.t('banking.accounts.card_creation_source.benefits'),
              value: 'salary',
            },
          ],
        },
      ],
    },
    {
      identifier: 'cardholderFilter',
      title: i18n.t('expenses_v2.cards.table.cardholder'),
      path: 'cardholderId',
      type: FilterType.Id,
      default: true,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: cardholderOptions,
        },
      ],
    },
  ]

  return (
    <>
      <DataTable
        source={source}
        disableDefaultActions
        filters={filters}
        emptyState={<EmptyState />}
        sort={{ path: 'id', direction: 'desc' }}
        actions={
          canAddCard ? (
            <Button.Router
              iconName="add"
              onClick={() => track(trackingEvents.expenses.gotToCreateCard)}
              type="primary"
              to={navigation.expenses.new_card.fullPath()}
            >
              {i18n.t('expenses_v2.cards.actions.add_new_card')}
            </Button.Router>
          ) : undefined
        }
      />
      {sidepanelCardId && (
        <CardSidepanel cardId={sidepanelCardId} onClose={() => setSidepanelCardId(null)} />
      )}
    </>
  )
}
